import { Component, HostBinding, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FixedAssetsScheduleTableType } from '../../enums/fixed-assets-schedule-table-type.enum';
import { FixedAssetsScheduleFormMovement } from '../../interfaces/fixed-assets-schedule-form-movement.interface';

@UntilDestroy()
@Component({
    selector: 'fixed-assets-schedule-table',
    templateUrl: 'fixed-assets-schedule-table.component.html',
    styleUrls: ['fixed-assets-schedule-table.component.scss'],
})
export class FixedAssetsScheduleTable {
    @Input() tableData: FixedAssetsScheduleFormMovement[] = [];

    @Input() tableType: FixedAssetsScheduleTableType | undefined;

    public _balanceOpeningDate: Date;
    public newPeriodDate: Date;
    @Input()
    set balanceOpeningDate(value: Date | undefined) {
        this._balanceOpeningDate = value;
        this.refreshNewPeriodDate();
    }

    // Start of the current period, set by the page.
    @Input()
    set periodStartDate(value: Date | undefined) {
        this._periodStartDate = value;
        this.refreshNewPeriodDate();
    }

    private _periodStartDate: Date | undefined;

    private refreshNewPeriodDate(): void {
        if (this._periodStartDate !== undefined) {
            this.newPeriodDate = this._periodStartDate;
            return;
        }
        if (this._balanceOpeningDate === undefined) {
            this.newPeriodDate = undefined;
            return;
        }
        const newDate = new Date(this._balanceOpeningDate);
        newDate.setDate(newDate.getDate() + 1);
        this.newPeriodDate = newDate;
    }
    get balanceOpeningDate(): Date | undefined {
        return this._balanceOpeningDate;
    }

    @Input() balanceClosingDate: Date;
    @Input() colTitles: string[];

    // Hides the previous book value column.
    @Input() noPreviousData = false;

    @Input() movements: UntypedFormArray | undefined;
    public _form: UntypedFormGroup | undefined;
    @Input()
    set form(value: UntypedFormGroup | undefined) {
        this._form = value;
    }

    get form(): UntypedFormGroup | undefined {
        return this._form;
    }

    @Input() reportLocale: string;

    @HostBinding('style.--fas-style-1') style1 = 'upper-alpha';
    @HostBinding('style.--fas-style-2') style2 = 'upper-roman';
    @HostBinding('style.--fas-style-3') style3 = 'decimal';
    @HostBinding('style.--fas-style-4') style4 = 'none';
    @HostBinding('style.--fas-style-5') style5 = 'none';
    @HostBinding('style.--fas-style-6') style6 = 'none';
    @HostBinding('style.--fas-style-7') style7 = 'none';
    @HostBinding('style.--fas-style-8') style8 = 'none';
    @HostBinding('style.--fas-style-9') style9 = 'none';
    @HostBinding('style.--fas-style-10') style10 = 'none';

    getClassForMovement(movement: any, index: number): string {
        let classes = '';

        // Add invalid-highlight class conditionally
        if (
            (this.tableType === 'COSTS' && !movement.get('costs').get('isValid').value) ||
            (this.tableType === 'DEPRECIATION' && !movement.get('depreciations').get('isValid').value) ||
            (this.tableType === 'VALUE APPRECIATION' && !movement.get('valueAppreciations').get('isValid').value)
        ) {
            classes += ' invalid-highlight';
        }

        const currentLevel = this.tableData[index].level;
        // Add level class
        classes += ` level-${this.tableData[index].level}`;
        if (!this.tableData[index].excludeFromCounting) {
            classes += this.tableData[index].disableNumbering
                ? ` indent-level-${this.tableData[index].level + 2}`
                : ` counter-level-${this.tableData[index].level + 2}`;
        } else {
            classes += ` counter-level-${this.tableData[index].level + 2}-excluded`;
        }

        // Add level class
        if (this.hasChildren(index)) {
            classes += ` hasChildren`;
        }

        return classes.trim();
    }

    hasChildren(index: number): boolean {
        const currentLevel = this.tableData[index].level;
        if (this.tableData.length > index + 1) {
            //There are more rows
            if (this.tableData[index + 1].level > currentLevel) {
                return true;
            }
        }
        return false;
    }
}
